import type { AxiosRequestConfig } from "axios";
import { RequestHttp } from "./http";
import { checkStatus } from "./helper/checkStatus";
import { markGlobalRequestError } from "@/utils/requestErrorState";

export type { CustomAxiosRequestConfig } from "./http";
export { RequestHttp } from "./http";

/** 读取 Pinia 持久化数据中的 token，迁移后由 Zustand 保持相同存储键。 */
export const getPersistedToken = () => {
    if (typeof localStorage === "undefined") {
        return "";
    }

    const storageKey = import.meta.env.VITE_USER_STORE_KEY || "zhihao-user";
    try {
        const persisted = JSON.parse(localStorage.getItem(storageKey) || "{}") as { token?: string };
        return persisted.token || "";
    } catch {
        return "";
    }
};

const requestConfig: AxiosRequestConfig = {
    // 开发环境交给 Vite 代理，生产环境使用既有 API 地址。
    baseURL: import.meta.env.DEV ? "" : import.meta.env.VITE_API_URL,
    timeout: 30000
};

/** 与旧版 Vue 请求层保持一致的 HTTP 状态提示文案。 */
const HTTP_ERROR_MESSAGES: Record<number, string> = {
    403: "当前账号无权限访问！",
    404: "你所访问的资源不存在！",
    405: "请求方式错误！请您稍后重试",
    408: "请求超时！请您稍后重试",
    500: "服务异常！请联系系统管理员！",
    502: "网关错误！请联系系统管理员！",
    503: "服务不可用！请联系系统管理员！",
    504: "网关超时！请您稍后重试"
};

/** 并发请求失败时只保留一条相同全局提示，避免页面被重复 Toast 覆盖。 */
const GLOBAL_MESSAGE_DEDUPE_WINDOW = 1000;
const recentGlobalMessageAt = new Map<string, number>();

const getHttpErrorMessage = (status: unknown) => {
    const normalizedStatus = typeof status === "number" ? status : Number(status);
    return Number.isFinite(normalizedStatus) ? HTTP_ERROR_MESSAGES[normalizedStatus] : undefined;
};

const shouldDispatchGlobalMessage = (message: string) => {
    const now = Date.now();
    const lastShownAt = recentGlobalMessageAt.get(message);

    // 清理过期记录，避免全局错误提示文案持续增长。
    for (const [cachedMessage, shownAt] of recentGlobalMessageAt) {
        if (now - shownAt >= GLOBAL_MESSAGE_DEDUPE_WINDOW) {
            recentGlobalMessageAt.delete(cachedMessage);
        }
    }

    if (lastShownAt !== undefined && now - lastShownAt < GLOBAL_MESSAGE_DEDUPE_WINDOW) {
        return false;
    }

    recentGlobalMessageAt.set(message, now);
    return true;
};

/** 读取统一请求错误文案，优先保留旧 Vue 的 errors 明细顺序。 */
const getGlobalErrorMessages = (error: unknown): string[] => {
    if (!error || typeof error !== "object") return ["请求失败"];

    const apiError = error as { message?: unknown; errors?: unknown; status?: unknown };
    const statusMessage = getHttpErrorMessage(apiError.status);
    if (statusMessage) return [statusMessage];

    const errorMessage = typeof apiError.message === "string" ? apiError.message : "";
    if (/timeout/i.test(errorMessage)) return ["请求超时！请您稍后重试"];
    if (/network error/i.test(errorMessage)) return ["网络错误！请您稍后重试"];

    // 顶层 message 常是“参数错误”等聚合文案，errors 明细才是用户可执行的真实原因。
    const errorMessages = Array.isArray(apiError.errors)
        ? apiError.errors.flatMap(item => {
              if (!item || typeof item !== "object") return [];
              const message = (item as { message?: unknown }).message;
              return typeof message === "string" && message.trim() ? [message] : [];
          })
        : [];

    if (errorMessages.length > 0) return errorMessages;

    return errorMessage.trim() ? [errorMessage] : ["请求失败"];
};

/** 将 Axios 错误转发给页面根节点，由 AntdApp 上下文负责渲染顶部全局消息。 */
export const notifyGlobalRequestError = (error: unknown) => {
    if (typeof window === "undefined") return;

    const status = error && typeof error === "object" ? (error as { status?: unknown }).status : undefined;
    // 401 已由 checkStatus 处理登录失效，避免再次弹出“请求失败”覆盖旧版提示。
    if (status === 401) return;

    for (const message of getGlobalErrorMessages(error)) {
        if (!shouldDispatchGlobalMessage(message)) continue;

        markGlobalRequestError(message);
        window.dispatchEvent(
            new CustomEvent("zhihao:global-message", {
                detail: { message, type: "error" }
            })
        );
    }
};

/** 将接口成功文案转发给根节点消息桥，确保所有成功响应使用统一顶部提示。 */
export const notifyGlobalRequestSuccess = (message: string) => {
    if (typeof window === "undefined") return;

    window.dispatchEvent(
        new CustomEvent("zhihao:global-message", {
            // 请求层成功提示只作兜底，由根消息桥等待页面提示后再决定是否展示。
            detail: { message, type: "success", fallback: true }
        })
    );
};

/** 主 API 客户端，保留原有 services 的 request.service 调用方式。 */
const request = new RequestHttp(requestConfig, {
    getToken: getPersistedToken,
    onError: notifyGlobalRequestError,
    onSuccess: notifyGlobalRequestSuccess,
    // 复用旧 Vue 的 401 处理：企业微信重新走 OAuth，普通浏览器跳转登录页。
    onUnauthorized: response => {
        void checkStatus(response);
    }
});

const shippingConfig: AxiosRequestConfig = {
    ...requestConfig,
    baseURL: import.meta.env.VITE_SHIPPING_API_URL,
    headers: {
        "X-Request-From": "shipping"
    }
};

/** 物流域 API 客户端，保留旧服务层的灰度版本头语义。 */
export const shippingRequestHttp = new RequestHttp(shippingConfig, {
    getToken: getPersistedToken,
    onError: notifyGlobalRequestError,
    onSuccess: notifyGlobalRequestSuccess,
    // 物流接口同样可能触发登录失效，必须与主 API 保持一致的自动重登行为。
    onUnauthorized: response => {
        void checkStatus(response);
    }
});

export default request;
